import React, { useState, useEffect, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { lang } from "src/language/Strings";

// Configure the worker to use local file
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.js";

interface PdfViewerProps {
  file: string | ArrayBuffer;
  overlayChildren?: React.ReactNode;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
  file,
  overlayChildren,
}) => {
  const [pdfDocument, setPdfDocument] =
    useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [useIframe, setUseIframe] = useState<boolean>(false);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<any>(null);

  // Load PDF document
  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);
        let loadingTask;
        if (typeof file === "string") {
          loadingTask = pdfjsLib.getDocument({
            url: file,
            cMapUrl: "/pdfjs/cmaps/",
            cMapPacked: true,
          });
        } else {
          loadingTask = pdfjsLib.getDocument(file);
        }

        const pdf = await loadingTask.promise;
        setPdfDocument(pdf);
        setNumPages(pdf.numPages);
        setCurrentPage(1);
      } catch (err) {
        console.error("Error loading PDF:", err);
        setError(
          `Failed to load PDF: ${
            err instanceof Error ? err.message : "Unknown error"
          }`
        );
        // If it's a CORS error, suggest using iframe fallback
        if (
          err instanceof Error &&
          (err.message.includes("CORS") || err.message.includes("cross-origin"))
        ) {
          setUseIframe(true);
        }
      } finally {
        setLoading(false);
      }
    };

    loadPdf();
  }, [file]);

  // Render current page
  useEffect(() => {
    if (!pdfDocument || !canvasRef.current) return;

    let isCancelled = false;

    const renderPage = async () => {
      try {
        // Cancel any existing render task
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        const page = await pdfDocument.getPage(currentPage);

        // Log comprehensive page data

        // Basic page properties
        const pageData = {
          pageNumber: currentPage,
          rotation: page.rotate,
          ref: page.ref,
          userUnit: page.userUnit,
          scale: scale,
        };

        // Get viewport data
        const viewport = page.getViewport({ scale, rotation: 0 });
        viewport.rotation = 0;

        const canvas = canvasRef.current!;
        const context = canvas.getContext("2d")!;


        // Check if this render was cancelled
        if (isCancelled) {
          return;
        }

        // Set canvas dimensions
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, canvas.width, canvas.height);

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
          canvas: canvas,
        };

        // Check again before rendering
        if (isCancelled) {

          return;
        }

        // Start the render task and store the reference
        renderTaskRef.current = page.render(renderContext);
        await renderTaskRef.current.promise;
        renderTaskRef.current = null;

      } catch (err) {
        if (!isCancelled) {
          console.error("Error rendering page: ", err);
          setError("Failed to render page");
        }
        renderTaskRef.current = null;
      }
    };

    renderPage();

    // Cleanup function to cancel ongoing renders
    return () => {
      isCancelled = true;
    };
  }, [pdfDocument, currentPage, scale])

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleViewerClick = () => {
    setShowOverlay(true);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if ((e.target as HTMLElement).id === e.currentTarget.id)
      setShowOverlay(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-lg">{lang.loading}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-4 w-auto h-full max-h-screen">
      <div className="relative w-auto h-full  overflow-auto">
        <div
          className="border border-gray-300 shadow-lg cursor-pointer w-full flex justify-center"
          onClick={handleViewerClick}
        >
          <canvas
            id="pdf-canvas"
            ref={canvasRef}
            className="max-w-full  w-auto h-auto"
          />
        </div>

        {showOverlay && (
          <div
            className="absolute inset-0 bg-PrimaryBlack bg-opacity-5 flex justify-center z-10"
            id="overlay-background"
            onClick={handleOverlayClick}
          >
            <div className="flex-row text-sm  max-h-6 w-full items-center justify-center align-middle text-center">
              <button
                onClick={goToPrevPage}
                id="previous-page-button"
                disabled={currentPage <= 1}
                className="px-3 py-1 bg-blue-500 text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-blue-600"
              >
                {lang.previous}
              </button>

              <span className="text-sm font-medium">
                {lang.pageNumber(currentPage, numPages)}
              </span>

              <button
                id="next-page-button"
                onClick={goToNextPage}
                disabled={currentPage >= numPages}
                className="px-3 py-1 bg-blue-500 text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-blue-600"
              >
                {lang.next}
              </button>
            </div>

            {overlayChildren}
          </div>
        )}
      </div>
    </div>
  );
};
